'use client';

import WhatsAppIcon from '@/components/icons/whatsapp-icon';
import { Button } from '@/components/ui/button';
import { SendEventMetapixel } from '@/lib/analytic-send-event';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

interface WhatsAppWidgetProps {
  phoneNumber?: string;
  message?: string;
}

const LS_KEY = 'waWidgetY';

export default function WhatsAppWidget({ phoneNumber, message }: WhatsAppWidgetProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const movedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const isPreview = searchParams.get('isPreview') === 'true';

  const DRAG_THRESHOLD = 6;
  const tapHighlightNone: React.CSSProperties = { WebkitTapHighlightColor: 'transparent' };

  const [isFixedAddToCart, setIsFixedAddToCart] = useState<boolean>(false);
  const [isFixedCheckoutCart, setIsFixedCheckoutCart] = useState<boolean>(false);
  const [posY, setPosY] = useState<number | null>(null);
  const [enabled, setEnabled] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const dragState = useRef<{ startY: number; startPosY: number; dragging: boolean }>({
    startY: 0,
    startPosY: 0,
    dragging: false,
  });

  const recomputeFixedFlags = useCallback(() => {
    setIsFixedAddToCart(!!document.getElementById('fixed-add-to-cart'));
    setIsFixedCheckoutCart(!!document.getElementById('fixed-checkout-cart'));
  }, []);

  const getBtnHeight = () => btnRef.current?.getBoundingClientRect().height ?? 56;

  const getBounds = useCallback(() => {
    const vh = window.innerHeight || 0;
    const btnH = getBtnHeight();
    const paddingTop = 180;

    let bottomSafe = 40;
    if (pathname.startsWith('/products/') && isFixedAddToCart) bottomSafe = 122;
    if (pathname.startsWith('/cart') && isFixedCheckoutCart) {
      bottomSafe = Math.max(bottomSafe, 160);
    }

    const maxTop = Math.max(vh - btnH - bottomSafe, paddingTop);
    return { maxTop, paddingTop };
  }, [isFixedAddToCart, isFixedCheckoutCart, pathname]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!btnRef.current || e.button !== 0) return;

    btnRef.current.setPointerCapture(e.pointerId);
    dragState.current.dragging = true;
    dragState.current.startY = e.clientY;
    dragState.current.startPosY = posY ?? 0;
    // Matiin text selection pas drag
    document.body.style.userSelect = 'none';
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragState.current.dragging) return;
    e.preventDefault();

    const delta = e.clientY - dragState.current.startY;
    if (Math.abs(delta) > DRAG_THRESHOLD) {
      movedRef.current = true;
    }

    const next = dragState.current.startPosY + delta;
    const { maxTop, paddingTop } = getBounds();
    setPosY(Math.min(Math.max(next, paddingTop), maxTop));
  };

  const endDrag = (e?: React.PointerEvent) => {
    if (!dragState.current.dragging) return;
    dragState.current.dragging = false;

    try {
      if (e && btnRef.current) btnRef.current.releasePointerCapture(e.pointerId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error releasing pointer capture:', error);
    }

    document.body.style.userSelect = '';

    if (movedRef.current) {
      suppressClickRef.current = true;
      // reset suppress setelah event loop berikutnya (200ms aman)
      setTimeout(() => {
        suppressClickRef.current = false;
        movedRef.current = false;
      }, 200);
    }

    // Persist
    if (posY != null) localStorage.setItem(LS_KEY, String(posY));
  };

  const handleClick = (e?: React.MouseEvent) => {
    if (suppressClickRef.current || dragState.current.dragging || movedRef.current) {
      // blokir click pasca drag
      e?.preventDefault?.();
      e?.stopPropagation?.();
      return;
    }
    SendEventMetapixel('Contact', {});
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
      (message as string) || ''
    )}`;
    window.open(url, '_blank');
  };

  const hiddenUntilReady = posY == null;

  useEffect(() => {
    recomputeFixedFlags();

    const observer = new MutationObserver(() => recomputeFixedFlags());
    observer.observe(document.body, { childList: true, subtree: true });

    const onResize = () => {
      setPosY((prev) => {
        if (prev === null) return prev;
        const { maxTop } = getBounds();
        return Math.min(Math.max(prev, 8), maxTop);
      });
    };
    window.addEventListener('resize', onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || posY != null) return;

    const saved = localStorage.getItem(LS_KEY);
    if (saved) {
      const y = Number(saved);
      const { maxTop, paddingTop } = getBounds();
      setPosY(Math.min(Math.max(y, paddingTop), maxTop));
      return;
    }

    const vh = window.innerHeight || 0;
    const btnH = getBtnHeight();

    let bottom = 64;
    if (pathname.startsWith('/products/') && isFixedAddToCart) bottom = 240;
    else if (pathname.startsWith('/cart') && isFixedCheckoutCart) bottom = 160;

    const top = Math.max(vh - btnH - bottom, 8);
    const { maxTop, paddingTop } = getBounds();
    setPosY(Math.min(Math.max(top, paddingTop), maxTop));
  }, [posY, getBounds, pathname, isFixedAddToCart, isFixedCheckoutCart]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const openEl = document.querySelector('[data-state="open"]');
      setIsModalOpen(!!openEl);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const exist = document.querySelector('[data-wa-widget]');
    if (exist && exist !== btnRef.current) setEnabled(false);
  }, []);

  if (!enabled) return null;

  return (
    <Button
      ref={btnRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onContextMenu={(e) => e.preventDefault()}
      className={cn(
        'fixed right-4 rounded-full bg-[#58C151] shadow-md hover:bg-[#25D366]',
        'cursor-grab touch-none select-none active:cursor-grabbing',
        'h-14 p-2 md:h-auto md:px-4 md:py-2.5 md:text-sm md:font-bold',
        hiddenUntilReady && 'pointer-events-none opacity-0',
        isModalOpen ? 'z-0' : 'z-[100]',
        isPreview && 'hidden'
      )}
      style={
        posY != null
          ? ({ top: posY, right: 16 } as React.CSSProperties)
          : ({ right: 16, ...tapHighlightNone } as React.CSSProperties)
      }
      onClick={handleClick}
      aria-label='WhatsApp'
      data-wa-widget
    >
      <span className='md:hidden'>
        <WhatsAppIcon width={40} height={40} />
      </span>

      <span className='hidden items-center gap-1 md:inline-flex'>
        <WhatsAppIcon className='size-5 shrink-0' />
        <span>WhatsApp Us</span>
        <ArrowRight className='size-5 shrink-0' />
      </span>
    </Button>
  );
}
